import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import CinematicNav from "@/components/CinematicNav";
import Footer from "@/components/Footer";
import { AriaPresenceTrigger } from "@/pages/TotalKlickHub";
import {
    ArrowRight, Sparkles, Brain, ShieldCheck, MessageCircle, Target,
    MapPin, Users, Network, Compass, Zap, Eye, ChevronRight
} from "lucide-react";

/**
 * /aria — Portale operativo Aria.
 *
 * NON è un wizard widget. NON è un form di configurazione.
 * È il punto di ingresso dove un imprenditore "assume Aria nella propria attività".
 *
 * Tre entrate condizionali:
 *  1. Crea la tua Aria → /aria/crea (wizard onboarding)
 *  2. Apri Workspace Aria → /app/aria (Dashboard owner)
 *  3. Ecosystem Control (solo superadmin) → /app/aria (per ora identico, evolverà)
 */

const PRINCIPLES = [
    {
        Icon: Eye,
        title: "Presenza, non chatbot",
        body: "Aria non risponde a domande. Ascolta, comprende, anticipa.",
    },
    {
        Icon: Brain,
        title: "Collaboratore virtuale",
        body: "Non un tool. Una risorsa che lavora 24/7 per la tua attività.",
    },
    {
        Icon: ShieldCheck,
        title: "Fedeltà assoluta",
        body: "Host First Policy: serve prima te. Sempre. Mai concorrenti.",
    },
    {
        Icon: Network,
        title: "Nodo dell'ecosistema",
        body: "Connessa al Network TotalKlick. Cresce con ogni nuova attività.",
    },
];

const ONBOARDING_STEPS = [
    { n: "01", title: "Chi sei?", desc: "Nome, categoria, sito, recapiti, città." },
    { n: "02", title: "Raccontati", desc: "Descrivi l'attività. Aria estrae servizi, prodotti, FAQ, keyword." },
    { n: "03", title: "Come deve parlare?", desc: "Tono di voce: professionale, amichevole, premium, diretta." },
    { n: "04", title: "Qual è il tuo obiettivo?", desc: "Lead, preventivi, prenotazioni, ordini, consulenze." },
    { n: "05", title: "Territorio", desc: "Città, provincia, regione, raggio operativo." },
    { n: "06", title: "Network", desc: "Aria aiuta prima te. Mai concorrenti. Mai prima del goal." },
];

export default function AriaPortal() {
    const { user } = useAuth();
    const isMaster = user && (user.role === "superadmin");

    useEffect(() => { document.title = "Aria — TotalKlick"; }, []);

    return (
        <div className="relative min-h-screen" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif", paddingTop: 44 }} data-testid="aria-portal">
            <CinematicNav />

            {/* HERO */}
            <section className="relative pt-32 pb-16 px-5 md:px-10 max-w-6xl mx-auto">
                {/* Background glow */}
                <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{
                    background: "radial-gradient(circle, rgba(79,139,255,0.15) 0%, transparent 70%)",
                    filter: "blur(60px)",
                }} />

                <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="relative text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6" style={{ background: "rgba(79,139,255,0.08)", border: "1px solid rgba(79,139,255,0.28)" }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-[#4F8BFF] animate-pulse" />
                        <span className="text-[10.5px] uppercase tracking-[0.28em] text-[#9EC3FF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                            assumi aria nella tua attività
                        </span>
                    </div>
                    <h1 className="text-white tracking-tight" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2.6rem, 6vw, 5.2rem)", letterSpacing: "-0.05em", lineHeight: 1 }}>
                        Una presenza<br /><span style={{
                            background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 65%, rgba(158,195,255,0.3) 100%)",
                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                        }}>che lavora per te</span>.
                    </h1>
                    <p className="text-white/65 mt-6 max-w-2xl mx-auto text-[15.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Non un chatbot. Non un plugin generico. Una collaboratrice virtuale che ascolta
                        i tuoi visitatori, raccoglie lead qualificati e impara cosa cercano
                        davvero le persone che entrano nel tuo sito.
                    </p>

                    {/* PRIMARY ACTION CARDS */}
                    <div className="grid md:grid-cols-2 gap-3 max-w-3xl mx-auto mt-10">
                        <ActionCard
                            to={user ? "/aria-widget" : "/signup"}
                            label="Crea la tua Aria"
                            sub="Presenta la tua attività · 6 step · 5 minuti"
                            Icon={Sparkles}
                            primary
                            testid="aria-action-create"
                        />
                        {user ? (
                            <ActionCard
                                to="/app/aria"
                                label="Apri Workspace Aria"
                                sub="Lead inbox · stats · cosa sta imparando Aria"
                                Icon={Compass}
                                testid="aria-action-workspace"
                            />
                        ) : (
                            <ActionCard
                                to="/login"
                                label="Hai già Aria? Accedi"
                                sub="Apri il tuo workspace"
                                Icon={Compass}
                                testid="aria-action-login"
                            />
                        )}
                    </div>

                    {/* Master entry (solo superadmin) */}
                    {isMaster && (
                        <Link to="/app/aria" className="inline-flex items-center gap-1.5 mt-5 text-[11.5px] text-[#FBBF24] hover:text-white transition tracking-[0.2em] uppercase" style={{ fontFamily: "JetBrains Mono, monospace" }} data-testid="aria-action-master">
                            <Zap size={11} /> ecosystem control center →
                        </Link>
                    )}
                </motion.div>
            </section>

            {/* PRINCIPI */}
            <Section eyebrow="cosa è aria" title="Quattro principi non negoziabili">
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {PRINCIPLES.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                            className="rounded-2xl p-5"
                            style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.07)", backdropFilter: "blur(14px)" }}
                            data-testid={`principle-${i}`}
                        >
                            <div className="grid place-items-center w-10 h-10 rounded-xl mb-3" style={{
                                background: "linear-gradient(135deg, rgba(79,139,255,0.22), rgba(79,139,255,0.06))",
                                border: "1px solid rgba(79,139,255,0.35)",
                            }}>
                                <p.Icon size={16} className="text-[#9EC3FF]" />
                            </div>
                            <div className="text-white text-[15px] mb-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{p.title}</div>
                            <p className="text-white/55 text-[12.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{p.body}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* COSA FA (host first explained) */}
            <Section eyebrow="host first policy" title="Prima aiuta te. Poi il mondo attorno a te.">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <motion.div initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                        <p className="text-white/65 text-[14.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Aria è fedele al 100% al tuo sito. Non propone mai concorrenti diretti.
                            Non suggerisce partner del Network prima che il tuo obiettivo sia raggiunto.
                        </p>
                        <p className="text-white/65 text-[14.5px] mt-4 leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Solo dopo che il visitatore ha lasciato il lead, e tu hai vinto la sua attenzione,
                            Aria può proporre partner complementari del Network — mai concorrenti, mai prima.
                        </p>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, x: 10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="space-y-1.5">
                        {[
                            { l: "visitatore", c: "rgba(255,255,255,0.55)" },
                            { l: "il tuo sito", c: "#4F8BFF", strong: true },
                            { l: "lead raccolto", c: "rgba(255,255,255,0.7)" },
                            { l: "obiettivo raggiunto", c: "#86EFAC", strong: true },
                            { l: "→ network", c: "rgba(255,255,255,0.4)", subtle: true },
                        ].map((s, i) => (
                            <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{
                                background: s.strong ? `${s.c}12` : "rgba(15,18,36,0.5)",
                                border: `1px solid ${s.strong ? `${s.c}55` : "rgba(255,255,255,0.06)"}`,
                            }}>
                                <span className="text-[10px] w-6 inline-block text-white/40 tracking-[0.2em]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{String(i + 1).padStart(2, "0")}</span>
                                <span className="text-[14px]" style={{ color: s.c, fontFamily: "Geist, sans-serif", fontWeight: s.strong ? 500 : 300, opacity: s.subtle ? 0.6 : 1 }}>{s.l}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </Section>

            {/* ONBOARDING STEPS */}
            <Section eyebrow="presenta la tua attività" title="Sei step per dare vita ad Aria">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {ONBOARDING_STEPS.map((s, i) => (
                        <motion.div
                            key={s.n}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.06 }}
                            className="rounded-2xl p-4"
                            style={{ background: "rgba(15,18,36,0.5)", border: "1px solid rgba(255,255,255,0.06)" }}
                            data-testid={`onboarding-step-${s.n}`}
                        >
                            <div className="flex items-baseline gap-2 mb-2">
                                <span className="text-[10px] tracking-[0.28em] text-[#4F8BFF]" style={{ fontFamily: "JetBrains Mono, monospace" }}>{s.n}</span>
                                <span className="text-white text-[14px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{s.title}</span>
                            </div>
                            <p className="text-white/55 text-[12.5px] leading-snug" style={{ fontFamily: "Outfit, sans-serif" }}>{s.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* WHAT YOU GET (workspace preview) */}
            <Section eyebrow="cosa avrai" title="Il tuo Workspace Aria">

                {/* === SCEGLI COME VUOI VIVERE ARIA (4 modalità Phase 6) === */}
                <div className="mb-10">
                    <div className="text-[10.5px] uppercase tracking-[0.28em] text-white/40 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>scegli come vuoi vivere aria</div>
                    <h3 className="text-white mb-6" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)", letterSpacing: "-0.025em", lineHeight: 1.15 }}>4 modalità. Una sola stringa JavaScript.</h3>
                    <AriaModeChooser />
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                    {[
                        { Icon: Users, t: "Lead Inbox", d: "Card persona reale. Intent hot/warm/cold. Summary AI. Tag automatici. Future needs." },
                        { Icon: Brain, t: "Cosa sta imparando Aria", d: "Top richieste, domande frequenti, bisogni emergenti. La voce del tuo pubblico." },
                        { Icon: Target, t: "Opportunità", d: "Partner suggeriti, collaborazioni, lead da richiamare oggi, network match." },
                    ].map((w, i) => (
                        <motion.div
                            key={w.t}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            className="rounded-2xl p-5"
                            style={{ background: "rgba(15,18,36,0.55)", border: "1px solid rgba(255,255,255,0.07)", backdropFilter: "blur(14px)" }}
                        >
                            <div className="grid place-items-center w-11 h-11 rounded-xl mb-3" style={{
                                background: "linear-gradient(135deg, rgba(79,139,255,0.22), rgba(79,139,255,0.06))",
                                border: "1px solid rgba(79,139,255,0.35)",
                            }}>
                                <w.Icon size={16} className="text-[#9EC3FF]" />
                            </div>
                            <div className="text-white text-[16px] mb-1.5" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{w.t}</div>
                            <p className="text-white/55 text-[13px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{w.d}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* === CHAT INLINE — Aria sempre aperta nel portale === */}
            <Section eyebrow="parla con aria adesso" title="O semplicemente inizia a chattare.">
                <div className="grid lg:grid-cols-5 gap-6 items-stretch">
                    <div className="lg:col-span-2 flex flex-col justify-center">
                        <p className="text-white/65 text-[14.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>
                            Aria è già qui. Provala. Falle domande sul tuo settore, su come potrebbe
                            aiutarti, su cosa raccoglierà per te. È il modo migliore per capire
                            se fa per te.
                        </p>
                        <ul className="mt-5 space-y-2.5" data-testid="aria-portal-chat-bullets">
                            {[
                                "Capisce il tuo settore in 2 frasi",
                                "Ti guida verso una configurazione realistica",
                                "Risponde in italiano, asciutta e concreta",
                            ].map((t, i) => (
                                <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    <span className="mt-1.5 w-3.5 h-3.5 rounded-sm border border-[#9EC3FF]/60 flex-none" style={{ background: "rgba(79,139,255,0.08)" }} />
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="lg:col-span-3">
                        <InlineAriaChat />
                    </div>
                </div>
            </Section>

            {/* FINAL — al posto del form semplice, mostriamo l'esperienza Cinematic 4
                  Quando l'utente arriva in fondo alla pagina, la presenza si attiva.
                  È la stessa identica esperienza della Home e di /aria/demo/cinematic. */}
            <section className="relative px-5 md:px-10 max-w-4xl mx-auto pt-16 pb-6 text-center">
                <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
                    <div className="text-[10.5px] uppercase tracking-[0.32em] mb-3" style={{ color: "#9EC3FF", fontFamily: "JetBrains Mono, monospace" }}>
                        prova adesso
                    </div>
                    <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4.5vw, 3.4rem)", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                        Pronto a dare<br />
                        <span style={{
                            background: "linear-gradient(180deg, #FFFFFF 0%, #9EC3FF 70%, rgba(158,195,255,0.3) 100%)",
                            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                        }}>una presenza alla tua attività?</span>
                    </h2>
                    <div className="text-white/65 text-[14px] mt-5 max-w-xl mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Scorri ancora un attimo. Aria si presenta da sola.
                    </div>
                </motion.div>
            </section>

            {/* Spacer + AriaPresenceTrigger (lo stesso layer della Home).
                  La presenza si attiva via IntersectionObserver quando l'ancora è in viewport. */}
            <section className="relative max-w-4xl mx-auto" style={{ height: "60vh" }}>
                <AriaPresenceTrigger />
            </section>

            <Footer variant="hub" />
        </div>
    );
}

function ActionCard({ to, label, sub, Icon, primary, testid }) {
    return (
        <Link
            to={to}
            className="group rounded-2xl p-5 text-left transition hover:scale-[1.015] relative overflow-hidden"
            style={primary ? {
                background: "linear-gradient(135deg, rgba(79,139,255,0.22), rgba(43,90,255,0.1))",
                border: "1px solid rgba(79,139,255,0.45)",
                boxShadow: "0 25px 60px -16px rgba(79,139,255,0.45)",
            } : {
                background: "rgba(15,18,36,0.55)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(14px)",
            }}
            data-testid={testid}
        >
            <div className="flex items-start gap-3">
                <div className="grid place-items-center w-11 h-11 rounded-xl flex-none" style={{
                    background: primary ? "rgba(255,255,255,0.12)" : "rgba(79,139,255,0.12)",
                    border: `1px solid ${primary ? "rgba(255,255,255,0.18)" : "rgba(79,139,255,0.3)"}`,
                }}>
                    <Icon size={17} className={primary ? "text-white" : "text-[#9EC3FF]"} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-white text-[16px] mb-0.5" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{label}</div>
                    <div className="text-white/60 text-[12px]" style={{ fontFamily: "Outfit, sans-serif" }}>{sub}</div>
                </div>
                <ChevronRight size={15} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition flex-none mt-1" />
            </div>
        </Link>
    );
}

function Section({ eyebrow, title, children }) {
    return (
        <section className="px-5 md:px-10 max-w-6xl mx-auto py-16">
            <div className="mb-7">
                <div className="text-[10.5px] uppercase tracking-[0.28em] text-white/40 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>{eyebrow}</div>
                <h2 className="text-white" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(1.8rem, 3.6vw, 2.6rem)", letterSpacing: "-0.03em", lineHeight: 1.08 }}>{title}</h2>
            </div>
            {children}
        </section>
    );
}

/**
 * InlineAriaChat — versione always-on della chat Aria, dentro la pagina /aria.
 * Più ampia, testo grande, stessi endpoint /api/hub/ai-guide del floating widget.
 */
function InlineAriaChat() {
    const [messages, setMessages] = React.useState([
        { role: "assistant", content: "Ciao, sono Aria. Raccontami cosa fai e che obiettivo hai sul tuo sito — vediamo se posso aiutarti." },
    ]);
    const [input, setInput] = React.useState("");
    const [sending, setSending] = React.useState(false);
    const [sessionId, setSessionId] = React.useState(null);
    const listRef = React.useRef(null);

    React.useEffect(() => {
        const el = listRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, sending]);

    const send = async (text) => {
        const t = (text ?? input).trim();
        if (!t || sending) return;
        const next = [...messages, { role: "user", content: t }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const res = await fetch(`${""}/api/hub/ai-guide`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: t, history: messages.slice(-6) }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || "AI offline");
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Mi spiace, c'è stato un intoppo. Riprova fra poco." }]);
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="rounded-3xl overflow-hidden flex flex-col"
            style={{
                height: 540,
                background: "linear-gradient(180deg, rgba(15,18,36,0.85), rgba(10,12,22,0.85))",
                border: "1px solid rgba(79,139,255,0.28)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 30px 80px -20px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)",
            }}
            data-testid="aria-portal-inline-chat"
        >
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-white/5 flex-none">
                <div className="w-9 h-9 rounded-full grid place-items-center" style={{ background: "linear-gradient(135deg, #4F8BFF, #2B5AFF)", boxShadow: "0 0 18px -2px rgba(79,139,255,0.6)" }}>
                    <Sparkles size={15} className="text-white" />
                </div>
                <div>
                    <div className="text-white text-[14.5px]" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>Aria · always on</div>
                    <div className="text-[11px] text-emerald-400 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
                    </div>
                </div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                            className="max-w-[85%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed"
                            style={{
                                background: m.role === "user"
                                    ? "linear-gradient(135deg, rgba(79,139,255,0.22), rgba(43,90,255,0.18))"
                                    : "rgba(255,255,255,0.05)",
                                color: "rgba(255,255,255,0.95)",
                                border: m.role === "user" ? "1px solid rgba(79,139,255,0.30)" : "1px solid rgba(255,255,255,0.06)",
                            }}
                            data-testid={`aria-portal-msg-${m.role}`}
                        >
                            <PortalRenderMessage content={m.content} />
                        </div>
                    </div>
                ))}
                {sending && (
                    <div className="flex justify-start">
                        <div className="px-3.5 py-2.5 rounded-2xl" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <span className="inline-flex gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "120ms" }} />
                                <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "240ms" }} />
                            </span>
                        </div>
                    </div>
                )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 px-4 py-3 border-t border-white/5 flex-none">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Chiedi qualcosa ad Aria…"
                    disabled={sending}
                    className="flex-1 bg-transparent text-white text-[15px] placeholder:text-white/35 focus:outline-none px-2"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                    data-testid="aria-portal-input"
                />
                <button
                    type="submit"
                    disabled={!input.trim() || sending}
                    className="w-10 h-10 rounded-full grid place-items-center flex-none transition disabled:opacity-40"
                    style={{ background: input.trim() ? "linear-gradient(135deg, #4F8BFF, #2B5AFF)" : "rgba(255,255,255,0.06)" }}
                    data-testid="aria-portal-send"
                    aria-label="Invia"
                >
                    <ArrowRight size={16} className="text-white" />
                </button>
            </form>
        </div>
    );
}

function PortalRenderMessage({ content }) {
    if (!content) return null;
    const lines = String(content).split(/\r?\n/);
    const out = [];
    let buf = [];
    const flush = (k) => {
        if (!buf.length) return;
        out.push(
            <ul key={`l-${k}`} className="my-1.5 space-y-1.5">
                {buf.map((t, i) => (
                    <li key={i} className="flex items-start gap-2">
                        <span className="mt-1 w-3.5 h-3.5 rounded-sm border border-[#9EC3FF]/60 flex-none" style={{ background: "rgba(79,139,255,0.05)" }} />
                        <span className="flex-1">{t}</span>
                    </li>
                ))}
            </ul>
        );
        buf = [];
    };
    lines.forEach((ln, i) => {
        const m = ln.match(/^\s*▢\s+(.*)$/);
        if (m) buf.push(m[1].trim());
        else { flush(i); if (ln.trim()) out.push(<p key={`p-${i}`} className="mb-1.5 last:mb-0">{ln}</p>); }
    });
    flush("end");
    return <>{out}</>;
}

/**
 * AriaModeChooser — 4 card per scegliere come vivere Aria.
 * Free → register widget gratuito. Premium → log upgrade intent + toast.
 */
function AriaModeChooser() {
    const [plans, setPlans] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [activating, setActivating] = React.useState(null);
    const [msg, setMsg] = React.useState(null);
    const [err, setErr] = React.useState("");

    React.useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${""}/api/widget/plans`);
                const data = await res.json();
                setPlans(data);
            } catch (e) {
                setErr("Impossibile caricare le modalità.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const accents = {
        free:      { c1: "#4F8BFF", c2: "#2B5AFF", glow: "rgba(79,139,255,0.35)" },
        welcome:   { c1: "#86EFAC", c2: "#10B981", glow: "rgba(134,239,172,0.32)" },
        full:      { c1: "#A855F7", c2: "#7C3AED", glow: "rgba(168,85,247,0.32)" },
        cinematic: { c1: "#FF1A5C", c2: "#A855F7", glow: "rgba(255,26,92,0.34)" },
    };
    const ctaLabels = {
        free: "Inizia gratis",
        welcome: "Attiva Welcome",
        full: "Attiva Experience",
        cinematic: "Attiva Cinematic",
    };

    const activate = async (mode) => {
        setActivating(mode);
        setMsg(null); setErr("");
        try {
            if (mode === "free") {
                // Free → onboarding /aria-widget (login + register)
                window.location.href = "/aria-widget";
                return;
            }
            // Premium → log intent (richiede auth)
            const token = window.localStorage.getItem("auth_token");
            if (!token) {
                window.location.href = `/login?redirect=${encodeURIComponent("/aria#modes")}`;
                return;
            }
            // Per attivare un mode su un widget specifico servirebbe widget_token,
            // ma da landing non lo abbiamo: registriamo un upgrade-intent generico via endpoint pubblico
            const res = await fetch(`${""}/api/public/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    tenant_name: "Aria Mode Request",
                    admin_email: "richiedente@placeholder.com",
                    main_pain: `[Aria Mode Activation] Richiesta attivazione modalità: ${mode}`,
                    gdpr_consent: true,
                    source: `aria-mode-${mode}`,
                }),
            });
            if (!res.ok) throw new Error("Errore");
            setMsg({ mode, label: ctaLabels[mode] });
        } catch (e) {
            setErr("Riprova tra un momento.");
        } finally {
            setActivating(null);
        }
    };

    if (loading) return <div className="text-white/50 text-[13px]">Caricamento modalità…</div>;

    return (
        <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="aria-mode-chooser">
                {plans.map((p, i) => {
                    const a = accents[p.mode] || accents.free;
                    const isPremium = p.mode !== "free";
                    return (
                        <motion.div
                            key={p.mode}
                            initial={{ opacity: 0, y: 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.06 }}
                            className="rounded-2xl p-5 relative overflow-hidden flex flex-col"
                            style={{
                                background: "rgba(15,18,36,0.55)",
                                border: `1px solid ${isPremium ? "rgba(255,255,255,0.10)" : "rgba(79,139,255,0.32)"}`,
                                backdropFilter: "blur(14px)",
                                boxShadow: `0 20px 50px -16px ${a.glow}`,
                                minHeight: 230,
                            }}
                            data-testid={`aria-mode-card-${p.mode}`}
                        >
                            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${a.c1}55 0%, transparent 70%)`, filter: "blur(24px)", opacity: 0.6 }} />
                            <div className="relative flex-1">
                                <div className="mono text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: a.c1, fontFamily: "JetBrains Mono, monospace" }}>{p.mode}</div>
                                <div className="text-white text-[18px] mb-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 500 }}>{p.label}</div>
                                <div className="text-white/85 text-[13px] mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                                    {p.price_eur === 0 ? "Gratuita per sempre" : `da €${p.price_eur}/mese`}
                                </div>
                                <p className="text-white/65 text-[12.5px] leading-relaxed" style={{ fontFamily: "Outfit, sans-serif" }}>{p.blurb}</p>
                            </div>
                            <div className="relative flex flex-col gap-2 mt-4">
                                <Link
                                    to={`/aria/demo/${p.mode}`}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[12.5px] transition hover:scale-[1.02]"
                                    style={{
                                        background: "rgba(255,255,255,0.06)",
                                        border: `1px solid ${a.c1}50`,
                                        color: "#fff",
                                        fontFamily: "Geist, sans-serif",
                                        fontWeight: 500,
                                    }}
                                    data-testid={`aria-mode-demo-${p.mode}`}
                                >
                                    <Eye size={12} /> Guarda la demo
                                </Link>
                                <button
                                    onClick={() => activate(p.mode)}
                                    disabled={activating === p.mode}
                                    className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-white text-[12.5px] font-medium transition hover:scale-[1.02] disabled:opacity-50"
                                    style={{
                                        background: `linear-gradient(135deg, ${a.c1}, ${a.c2})`,
                                        fontFamily: "Geist, sans-serif",
                                        boxShadow: `0 10px 30px -10px ${a.glow}`,
                                    }}
                                    data-testid={`aria-mode-cta-${p.mode}`}
                                >
                                    {activating === p.mode ? "..." : ctaLabels[p.mode]} <ArrowRight size={12} />
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
            {msg && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-5 rounded-2xl p-4 text-[13px]"
                    style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", color: "#86EFAC" }}
                    data-testid="aria-mode-success"
                >
                    Richiesta <b className="text-white">{msg.label}</b> registrata. Ti contattiamo entro 24h per l&apos;onboarding.
                </motion.div>
            )}
            {err && <div className="mt-3 text-[12.5px] text-rose-300" data-testid="aria-mode-error">{err}</div>}
        </>
    );
}

// suppress unused imports
ShieldCheck; MapPin; Network; Compass; Zap; // eslint-disable-line no-unused-expressions
